import { documentId, orderBy } from "firebase/firestore";
import React, { useMemo } from "react";
import { Col, Form, Row } from "react-bootstrap";
import Select, { createFilter } from "react-select";

import {
    useAppCollectionGroup,
    useDockCollection,
    useDroneConfigCollectionGroup,
    useLocationCollection,
    useOrganizationCollection,
    usePayloadCollection,
    useRoutineCollection,
    useTaskCollection,
} from "../../api/firestoreWrappers.generated.jsx";

export const COLLECTION_CONFIG = {
    droneConfigs: {
        label: "Drones",
        filters: [{ key: "droneId", title: "Drones" }],
    },
    apps: {
        label: "Apps",
        filters: [{ key: "appId", title: "Apps" }],
    },
    routines: {
        label: "Routines",
        filters: [
            { key: "organizationId", title: "Organizations" },
            { key: "locationId", title: "Locations" },
            { key: "routineId", title: "Routines" },
        ],
    },
    docks: {
        label: "Docks",
        filters: [
            { key: "organizationId", title: "Organizations" },
            { key: "locationId", title: "Locations" },
            { key: "dockId", title: "Docks" },
        ],
    },
    locations: {
        label: "Locations",
        filters: [
            { key: "organizationId", title: "Organizations" },
            { key: "locationId", title: "Locations" },
        ],
    },
    tasks: {
        label: "Tasks",
        filters: [
            { key: "organizationId", title: "Organizations" },
            { key: "locationId", title: "Locations" },
            { key: "taskId", title: "Tasks" },
        ],
    },
    payloads: {
        label: "Payloads",
        filters: [{ key: "payloadId", title: "Payloads" }],
    },
};

const COLLECTION_OPTIONS = Object.entries(COLLECTION_CONFIG)
    .map(([value, cfg]) => ({
        value,
        label: cfg.label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const FILTER_FIELDS = Object.fromEntries(
    Object.entries(COLLECTION_CONFIG).map(([k, v]) => [k, v.filters]),
);

function toSelectOptions(items, formatLabel = (item) => item.name) {
    if (!items) return [];

    const active = [];
    const deleted = [];

    for (const item of items) {
        (item.deleted ? deleted : active).push(item);
    }

    // Show deleted at the end
    return [...active, ...deleted].map((item) => ({
        value: item.id,
        label: formatLabel(item),
    }));
}

function withDeletedSuffix(item) {
    return item.deleted ? `${item.name} (deleted)` : item.name;
}

const FilterOption = React.memo(function FilterOption({
    show,
    value,
    options,
    placeholder,
    onChange,
    isClearable = true,
}) {
    if (!show) return null;

    return (
        <Select
            isClearable={isClearable}
            value={options.find((o) => o.value === value)}
            options={options}
            placeholder={placeholder}
            filterOption={createFilter({ stringify: (option) => option.label })} // Only match labels when typing
            onChange={(e) => onChange(e?.value)}
            styles={{
                menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                }),
            }}
        />
    );
});

export default function LogFilters({ className, filters, setFilters }) {
    const [droneConfigMap] = useDroneConfigCollectionGroup(
        [orderBy(documentId(), "asc")],
        { mode: "once", type: "map" },
    );
    const drones = useMemo(() => {
        return Object.keys(droneConfigMap ?? {})
            .filter((id) => id !== "presets")
            .map((id) => ({ id, name: id }));
    }, [droneConfigMap]);

    const [organizations] = useOrganizationCollection(
        [orderBy("name", "asc")],
        { mode: "once" },
    );
    const [locations] = useLocationCollection(
        filters.organizationId,
        [orderBy("name", "asc")],
        { mode: "once" },
    );
    const [routines] = useRoutineCollection(
        filters.organizationId,
        filters.locationId,
        [orderBy("name", "asc")],
        { mode: "once" },
    );
    const [docks] = useDockCollection(
        filters.organizationId,
        filters.locationId,
        [orderBy("name", "asc")],
        { mode: "once" },
    );
    const [apps] = useAppCollectionGroup([orderBy("name", "asc")], {
        mode: "once",
    });
    const [tasks] = useTaskCollection(
        filters.organizationId,
        filters.locationId,
        filters.taskId,
        [orderBy("name", "asc")],
        { mode: "once" },
    );
    const [payloads] = usePayloadCollection([orderBy("name", "asc")], {
        mode: "once",
    });

    const droneOptions = useMemo(() => toSelectOptions(drones), [drones]);
    const organizationOptions = useMemo(
        () => toSelectOptions(organizations, withDeletedSuffix),
        [organizations],
    );
    const locationOptions = useMemo(
        () => toSelectOptions(locations, withDeletedSuffix),
        [locations],
    );
    const routineOptions = useMemo(() => toSelectOptions(routines), [routines]);
    const dockOptions = useMemo(() => toSelectOptions(docks), [docks]);
    const appOptions = useMemo(() => toSelectOptions(apps), [apps]);
    const taskOptions = useMemo(() => toSelectOptions(tasks), [tasks]);
    const payloadOptions = useMemo(() => toSelectOptions(payloads), [payloads]);

    const OPTIONS_BY_FIELD = useMemo(
        () => ({
            droneId: droneOptions,
            organizationId: organizationOptions,
            locationId: locationOptions,
            routineId: routineOptions,
            dockId: dockOptions,
            appId: appOptions,
            taskId: taskOptions,
            payloadId: payloadOptions,
        }),
        [
            droneOptions,
            organizationOptions,
            locationOptions,
            routineOptions,
            dockOptions,
            appOptions,
            taskOptions,
            payloadOptions,
        ],
    );

    // Ensure activeCollection is something in the FILTER_FIELDS
    const activeCollection = FILTER_FIELDS[filters.collection]
        ? filters.collection
        : "droneConfigs";
    const fields = FILTER_FIELDS[activeCollection];

    function handleChange(fieldKey, value) {
        setFilters((prev) => {
            // Ensure collection has a value
            const collection =
                fieldKey === "collection"
                    ? value
                    : (prev.collection ?? "droneConfigs");
            return applyFilterChange(prev, collection, fieldKey, value);
        });
    }

    function applyFilterChange(prev, collection, changedFieldKey, value) {
        const fields = FILTER_FIELDS[collection];
        if (!fields) return { collection };

        const next = { collection };

        const changedIndex = fields.findIndex((f) => f.key === changedFieldKey);

        fields.forEach((field, i) => {
            if (i < changedIndex) {
                next[field.key] = prev[field.key]; // keep parents
            } else if (i === changedIndex) {
                next[field.key] = value; // apply change
            } else {
                next[field.key] = undefined; // clear children
            }
        });

        return next;
    }

    return (
        <Form className={className}>
            <Form.Group className="mt-2">
                <Row className="m-0">
                    <Col md={3} className="p-0 pe-md-1">
                        <FilterOption
                            show={true}
                            value={filters.collection}
                            options={COLLECTION_OPTIONS}
                            placeholder="Filter Types"
                            isClearable={false}
                            onChange={(value) =>
                                handleChange("collection", value)
                            }
                        />
                    </Col>
                    {fields.map((field, index) => {
                        const hasParentValue = Boolean(
                            filters[fields[index - 1]?.key],
                        );
                        const show = index === 0 || hasParentValue;

                        return (
                            <Col md={3} className="p-0 pe-md-1" key={field.key}>
                                <FilterOption
                                    show={show}
                                    value={filters[field.key]}
                                    options={OPTIONS_BY_FIELD[field.key]}
                                    placeholder={`Filter ${field.title}`}
                                    onChange={(value) =>
                                        handleChange(field.key, value)
                                    }
                                />
                            </Col>
                        );
                    })}
                </Row>
            </Form.Group>
        </Form>
    );
}
